import { useCallback, useEffect, useRef } from 'react'

function isVisible(document) {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

/**
 * Schedule ordinary refresh work without hidden-tab or overlapping requests.
 * The caller owns its confirmed data, so refreshes never clear useful content.
 *
 * `resetKey` names what the refresh asks for (a dashboard's window, say). When
 * it changes the lifecycle restarts: in-flight work for the old question is
 * aborted and the new one is asked at once, instead of waiting out the old
 * request and a full interval.
 */
export default function useVisibilityRefresh({
  refresh,
  enabled = true,
  intervalMs = null,
  initialRefresh = true,
  initialDelayMs = 0,
  refreshOnFocus = true,
  resetKey = null,
  environment = null,
}) {
  const lifecycleDocument = environment?.document ?? globalThis.document
  const lifecycleWindow = environment?.window ?? globalThis.window
  const visible = useCallback(() => isVisible(lifecycleDocument), [lifecycleDocument])
  const requestedDelayRef = useRef(null)
  const refreshRef = useRef(refresh)
  const enabledRef = useRef(enabled)
  const intervalRef = useRef(intervalMs)
  const mountedRef = useRef(false)
  const timerRef = useRef(null)
  const inFlightRef = useRef(null)
  const controllerRef = useRef(null)
  const runRef = useRef(null)

  refreshRef.current = refresh
  enabledRef.current = enabled
  intervalRef.current = intervalMs

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const schedule = useCallback((delay = intervalRef.current) => {
    clearTimer()
    if (!mountedRef.current || !enabledRef.current || !visible()) return
    if (!Number.isFinite(delay) || delay < 0) return
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      void runRef.current?.('interval').catch(() => {})
    }, delay)
  }, [clearTimer, visible])

  const run = useCallback((reason = 'manual') => {
    if (!mountedRef.current || !enabledRef.current) return Promise.resolve(false)
    if (reason !== 'manual' && !visible()) return Promise.resolve(false)
    if (inFlightRef.current && !controllerRef.current?.signal.aborted) return inFlightRef.current
    // React StrictMode and dependency changes clean up an effect before
    // mounting its replacement. An aborted predecessor must not suppress the
    // replacement's first refresh; its `finally` is identity-guarded below.
    if (controllerRef.current?.signal.aborted) {
      inFlightRef.current = null
      controllerRef.current = null
    }

    clearTimer()
    const controller = new AbortController()
    controllerRef.current = controller
    const pending = Promise.resolve()
      .then(() => refreshRef.current({ signal: controller.signal, reason }))
      .then(() => true)
      .finally(() => {
        if (inFlightRef.current !== pending) return
        inFlightRef.current = null
        controllerRef.current = null
        const requested = requestedDelayRef.current
        requestedDelayRef.current = null
        if (mountedRef.current && enabledRef.current && visible()) schedule(requested ?? intervalRef.current)
      })
    inFlightRef.current = pending
    return pending
  }, [clearTimer, schedule, visible])
  runRef.current = run

  useEffect(() => {
    mountedRef.current = true
    const onVisible = () => {
      if (!visible()) {
        clearTimer()
        return
      }
      void run('visible').catch(() => {})
    }
    const onFocus = () => {
      if (refreshOnFocus && visible()) void run('focus').catch(() => {})
    }

    lifecycleDocument.addEventListener('visibilitychange', onVisible)
    if (refreshOnFocus) lifecycleWindow.addEventListener('focus', onFocus)

    if (enabled) {
      if (initialRefresh) {
        if (initialDelayMs > 0) schedule(initialDelayMs)
        else void run('initial').catch(() => {})
      } else {
        schedule()
      }
    } else {
      clearTimer()
    }

    return () => {
      mountedRef.current = false
      clearTimer()
      requestedDelayRef.current = null
      controllerRef.current?.abort()
      lifecycleDocument.removeEventListener('visibilitychange', onVisible)
      if (refreshOnFocus) lifecycleWindow.removeEventListener('focus', onFocus)
    }
  }, [clearTimer, enabled, initialDelayMs, initialRefresh, intervalMs, refreshOnFocus, resetKey, run, schedule, visible, lifecycleDocument, lifecycleWindow])

  const refreshNow = useCallback(() => run('manual'), [run])
  // Cursor-driven readers can ask for their next due time without owning a
  // private timer. An in-flight refresh defers that wake until it settles.
  refreshNow.schedule = useCallback((delay) => {
    if (inFlightRef.current && Number.isFinite(delay)) {
      requestedDelayRef.current = requestedDelayRef.current == null ? delay : Math.min(requestedDelayRef.current, delay)
    } else schedule(delay)
  }, [schedule])
  return refreshNow
}
